import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { emptyLessonObj } from '@app/account/profile/user-profile.variables';
import { DictionaryService } from '@app/core/services/bff/dictionary.service';
import { findSubjectById, subjectPseudoId } from '@app/core/services/bff/helpers.service';
import { UserProfileBffService } from '@app/core/services/bff/user-profile.service';
import { LoadingBarService } from '@core/components/spinners/loading-bar/loading-bar.service';
import {
  AccountService,
  AuthService,
  ConfigService,
  ContentService,
  ContextService,
  FeaturesService,
  LanguageService,
  MetaService,
  ProfileService,
} from '@core/services';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import {
  EContentOrder,
  EContentPaneFormat,
  EContentPanelRoute,
  FindContentStrategyEnum,
  IContentPane,
  IFindContentFilter,
  ITeachingSubject,
  Language,
  otherSubjectId,
  Subject,
  SubjectCategoryEnum,
  UserService,
  UserServiceCategory,
  UserStatusEnum,
} from 'lingo2-models';
import { uniqBy } from 'lodash';
import { DeviceDetectorService } from 'ngx-device-detector';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
import { BaseUserProfileSubpageComponent } from '../base-user-profile-subpage.component';

interface ITeachingSubjectItem {
  value: string | number;
  title: string;
  subject: ITeachingSubject;
  total?: number;
}

@Component({
  selector: 'app-user-profile-lessons-subpage',
  templateUrl: './user-profile-lessons-subpage.component.html',
  styleUrls: ['./user-profile-lessons-subpage.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileLessonsSubpageComponent extends BaseUserProfileSubpageComponent implements OnInit {
  @Input() view: string;

  @Input()
  public set selectedSubjectId(selectedSubjectId: string) {
    this._selectedSubjectId = selectedSubjectId;
    this.init();
  }

  public get selectedSubjectId(): string {
    return this._selectedSubjectId;
  }

  @Output() onChangeSubject = new EventEmitter<string>();
  @Output() loaded = new EventEmitter<boolean>();
  public teachingSubjectsList: ITeachingSubjectItem[] = [];
  public filteredTeachingSubjectsList: ITeachingSubjectItem[] = [];
  public teachingSubjectIndex = -1;
  public teachingSubjectMore = false;
  public paneLessons: IContentPane;
  public isContent = true;

  public emptyLessonObj = emptyLessonObj;

  public activeServiceIndex = -1;
  public checkoutService: UserService;
  public isMediaPrepared = false;
  public teachingSubject: ITeachingSubject;
  public selectUserServicesFilter: UserServiceCategory;
  public openedUserService: UserService;
  public routes = EContentPanelRoute;

  private subjectId: string;
  private _selectedSubjectId: string;

  constructor(
    public errorNotificationService: ErrorNotificationService,
    protected router: Router,
    protected authService: AuthService,
    protected contextService: ContextService,
    protected contentService: ContentService,
    protected profileService: ProfileService,
    protected userProfileBff: UserProfileBffService,
    protected features: FeaturesService,
    protected dictionaryService: DictionaryService,
    protected languageService: LanguageService,
    protected meta: MetaService,
    public deviceService: DeviceDetectorService,
    protected loadingBar: LoadingBarService,
    protected cdr: ChangeDetectorRef,
  ) {
    super(
      router,
      authService,
      contextService,
      profileService,
      dictionaryService,
      languageService,
      userProfileBff,
      features,
      meta,
      loadingBar,
      cdr,
    );
  }

  private init(): void {
    this.loading = true;

    this.loading = false;
    this.subjectId = this.selectedSubjectId ? this.selectedSubjectId : this.router.url.split('/').pop();
    this.prepareCardByProfile();
    this.markForCheck();

    this.getSubjectId$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => this.initTeachingSubject(),
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
  }

  public ngOnInit(): void {
    this.init();
  }

  protected prepareCardByProfile() {
    if (!this._user || !this._profile || !this.subjects.length) {
      return;
    }

    this.accountRoute = AccountService.accountRoute(this._user).join('/');
    this.teachingSubjectsList = (this._profile.teachingSubjects || [])
      .filter((s) => s.subjectId > 0)
      .map((subject, index) => {
        if (+subject.subjectId === otherSubjectId) {
          return {
            value: index,
            title: subject.subjectOtherName || '---',
            route: AccountService.accountRoute(this._user, ['teaching', subjectPseudoId(subject)]),
            native: findSubjectById(this.subjects, subject)?.category_id === SubjectCategoryEnum.native_language,
            subject: {
              subject_id: subject.subjectId,
              subject: null,
              subject_other_name: subject.subjectOtherName,
              subject_other_id: subject.subjectOtherName,
              description: subject.description,
              video_url: subject.videoUrl,
              video_id: subject.videoId,
              video: null,
              video_mode: subject.videoMode,
            },
          };
        } else {
          const _subject = this.subjects.find((s) => s.id === +subject.subjectId);
          return {
            value: index,
            title: (_subject ? _subject.title : null) || '---',
            route: AccountService.accountRoute(this._user, ['teaching', subjectPseudoId(subject)]),
            native: findSubjectById(this.subjects, subject)?.category_id === SubjectCategoryEnum.native_language,
            subject: {
              subject_id: subject.subjectId,
              subject: null,
              subject_other_name: subject.subjectOtherName,
              subject_other_id: subject.subjectOtherName,
              description: subject.description,
              video_url: subject.videoUrl,
              video_id: subject.videoId,
              video: null,
              video_mode: subject.videoMode,
            },
          };
        }
      });

    this.teachingSubjectsList = uniqBy(this.teachingSubjectsList, (e) => e.subject.subject_id);

    const _filter: Partial<IFindContentFilter> = {
      use: FindContentStrategyEnum.profile,
      order: EContentOrder.date_desc,
      author_id: this._user.id,
    };

    this.teachingSubjectsList.forEach((el) => {
      if (el.subject.subject_other_id) {
        _filter.subject_id = Number(el.subject.subject_other_id);
      } else {
        _filter.subject_id = el.subject.subject_id;
      }

      // this.contentService
      //   .getContent(_filter, { page: 1, pageSize: 1, totalPages: 1, total: 0 })
      //   .pipe(takeUntil(this.destroyed$))
      //   .subscribe({
      //     next: (res) => {
      //       const objIndex = this.teachingSubjectsList.findIndex(
      //         (obj) => obj.subject.subject_id == el.subject.subject_id,
      //       );
      //       this.teachingSubjectsList[objIndex].total = res?.total;
      //       this.filteredTeachingSubjectsList = this.teachingSubjectsList.filter((el) => el.total > 0);
      //       this.detectChanges();
      //     },
      //     error: (error) => {
      //       this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      //     },
      //   });
    });

    this.initTeachingSubject();
    this.isMe = this._me && this._user && this._me?.id === this._user.id;
    this.isGuest = this._me && this._me?.status === UserStatusEnum.guest;
  }

  protected initTeachingSubject() {
    if (this.subjectId && this.teachingSubjectsList.length) {
      const item = this.teachingSubjectsList.find((s) => s.subject.subject_id === +this.subjectId);
      this.teachingSubject = item ? item.subject : null;

      this.loadLessons();
    } else {
      this.isContent = false;
    }
  }

  protected loadLessons() {
    if (!this._user || !this.teachingSubject) {
      return;
    }

    const _filter: Partial<IFindContentFilter> = {
      use: FindContentStrategyEnum.profile,
      order: EContentOrder.date_desc,
      author_id: this._user.id,
    };

    if (this.teachingSubject.subject_other_id) {
      _filter.subject_id = Number(this.teachingSubject.subject_other_id);
    } else {
      _filter.subject_id = this.teachingSubject.subject_id;
    }

    this.paneLessons = {
      order: 1,
      format: EContentPaneFormat['new-grid-universal-card'],
      loaded: false,
      infinity: true,
      new_grid_view: 'vertical-square',
      show_like_slider: false,
      max_element_count: 4,
      max_element_width: 200,
      default_element_width: 200,
      routerUrlCreate: '',
      replace_for_mobile: true,
      replace_for_mobile_pane: 'card-universal',
      contentFilter: _filter,
    };
  }

  public setSubjectId(subjectId: string) {
    this._selectedSubjectId = subjectId;
    this.onChangeSubject.emit(subjectId);
    this.init();
  }

  private get getSubjectId$(): Observable<string> {
    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event: NavigationEnd) => {
        this.subjectId = this.selectedSubjectId ? this.selectedSubjectId : this.parseSubjectIdFromUrl(event.url);
        return this.subjectId;
      }),
    );
  }

  public checkContent(e: boolean) {
    this.isContent = e;
    this.loaded.emit(true);
  }

  private parseSubjectIdFromUrl(url: string): string {
    return this.router.url.split('/').pop();
  }
}
